import { AudioTrack } from "providers/AudioProvider/types";
import { SelectedTranslatedTrack } from "providers/AudioProvider/types";
import { voicingSounds } from "providers/AudioProvider/dubSounding";

/**
 * `allowCurrentOverlap` — «без автоправок» лента рисует файлы как есть, и озвучка может уже
 * наезжать на соседку. Границы «от края соседки» тогда не содержат её собственного места, и
 * любой шаг перетаскивания — даже уменьшающий наезд — отклонялся бы: переводчик не смог бы
 * сам поставить то, что иначе раздвинет машина (наезд снимается сдвигом следующей позже, правило
 * владельца 01.10.2026). С флагом границы расширяются до текущего места озвучки: отодвинуть и
 * укоротить можно, наехать сильнее — нет. Без наезда флаг ничего не меняет.
 */
const holdCurrentPlace = (
    bounds: { min: number; max: number },
    track: AudioTrack | undefined,
    allowCurrentOverlap: boolean | undefined,
) => {
    if (!allowCurrentOverlap || !track) return bounds;
    return {
        min: Math.min(bounds.min, track.start),
        max: Math.max(bounds.max, track.start + track.length),
    };
};

// Границы — края соседних ЗВУЧАЩИХ озвучек: у реплики без файла и у снятой с озвучки (⊘, её
// старый `file` остаётся на записи) в видео ничего не звучит, и мешать перетаскиванию им нечем.
export const getMaxLength = ({
    ind,
    tracks,
    durationFull,
    allowCurrentOverlap,
}: {
    ind: number;
    tracks: AudioTrack[];
    durationFull: number;
    allowCurrentOverlap?: boolean;
}) => {
    let min = 0;
    for (let i = ind - 1; i >= 0; i--) {
        if (voicingSounds(tracks[i])) {
            min = tracks[i].start + tracks[i].length;
            break;
        }
    }

    let max = durationFull;
    for (let i = ind + 1; i < tracks.length; i++) {
        if (voicingSounds(tracks[i])) {
            max = tracks[i].start;
            break;
        }
    }
    return holdCurrentPlace({ min, max }, tracks[ind], allowCurrentOverlap);
};

export const getSelectedMaxMinLength = ({
    ind,
    tracks,
    durationFull,
    selectedTracks,
    allowCurrentOverlap,
}: {
    ind: number;
    tracks: AudioTrack[];
    durationFull: number;
    selectedTracks: SelectedTranslatedTrack[];
    allowCurrentOverlap?: boolean;
}) => {
    if (selectedTracks.length < 2) {
        return getMaxLength({ ind, tracks, durationFull, allowCurrentOverlap });
    }
    const currentTrack = tracks[ind];
    const startTrack = selectedTracks[0];
    const lastTrack = selectedTracks[selectedTracks.length - 1];
    let min = 0;
    let max = durationFull;

    for (let i = startTrack.ind - 1; i >= 0; i--) {
        if (voicingSounds(tracks[i])) {
            const prevEnd = tracks[i].start + tracks[i].length;
            const minLength = startTrack.track.start - prevEnd;
            min = currentTrack.start - minLength;
            break;
        }
    }

    for (let i = lastTrack.ind + 1; i < tracks.length; i++) {
        if (voicingSounds(tracks[i])) {
            const lastStart = tracks[i].start;
            const maxLength = lastStart - (lastTrack.track.start + lastTrack.track.length);
            max = currentTrack.start + currentTrack.length + maxLength;
            break;
        }
    }

    return holdCurrentPlace({ min, max }, currentTrack, allowCurrentOverlap);
};
